import { useState, useRef, useEffect, useCallback } from 'react';

interface NumericInputModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (value: number) => void;
  title: string;
  /** Icon/emoji to display next to title */
  icon?: string;
  /** Placeholder text for input */
  placeholder?: string;
  /** Submit button text */
  submitLabel?: string;
  /** Theme color variant */
  variant?: 'damage' | 'heal' | 'temp' | 'default';
  /** Minimum allowed value (default: 1) */
  min?: number;
  /** Maximum allowed value */
  max?: number;
}

const VARIANT_STYLES = {
  damage: {
    border: 'border-error/50',
    button: 'bg-error hover:bg-error/80 text-white',
    icon: '💥',
    glow: 'shadow-error/20',
  },
  heal: {
    border: 'border-success/50',
    button: 'bg-success hover:bg-success/80 text-white',
    icon: '💚',
    glow: 'shadow-success/20',
  },
  temp: {
    border: 'border-class-wizard/50',
    button: 'bg-class-wizard hover:bg-class-wizard/80 text-white',
    icon: '🛡️',
    glow: 'shadow-class-wizard/20',
  },
  default: {
    border: 'border-accent-gold/50',
    button: 'bg-accent-gold hover:bg-accent-gold-light text-background-primary',
    icon: '✨',
    glow: 'shadow-accent-gold/20',
  },
};

export function NumericInputModal({
  isOpen,
  onClose,
  onSubmit,
  title,
  icon,
  placeholder = 'Enter amount',
  submitLabel = 'Apply',
  variant = 'default',
  min = 1,
  max,
}: NumericInputModalProps) {
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const styles = VARIANT_STYLES[variant];
  const displayIcon = icon ?? styles.icon;

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen) {
      setValue('');
      setError(null);
      // Small delay to ensure modal is rendered
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleSubmit = useCallback(() => {
    const num = parseInt(value, 10);
    if (isNaN(num)) {
      setError('Please enter a valid number');
      return;
    }
    if (num < min) {
      setError(`Minimum value is ${min}`);
      return;
    }
    if (max !== undefined && num > max) {
      setError(`Maximum value is ${max}`);
      return;
    }
    onSubmit(num);
    onClose();
  }, [value, min, max, onSubmit, onClose]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-background-primary/80 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className={`relative w-full max-w-xs bg-background-card border-2 ${styles.border}
          rounded-lg shadow-2xl ${styles.glow} overflow-hidden
          animate-in fade-in zoom-in-95 duration-200`}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-4 py-3 bg-background-elevated border-b border-background-primary">
          <span className="text-2xl">{displayIcon}</span>
          <h2 className="font-display text-lg text-text-primary tracking-wide">{title}</h2>
        </div>

        {/* Input */}
        <div className="p-4">
          <input
            ref={inputRef}
            type="number"
            inputMode="numeric"
            pattern="[0-9]*"
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              setError(null);
            }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            min={min}
            max={max}
            className={`w-full px-4 py-3 text-center text-2xl font-mono font-bold
              bg-background-primary border-2 ${error ? 'border-error' : 'border-background-elevated'}
              rounded-lg text-text-primary placeholder:text-text-muted
              focus:outline-none focus:border-accent-bronze transition-colors
              [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none
              [&::-webkit-inner-spin-button]:appearance-none`}
          />

          {/* Error message */}
          {error && (
            <p className="mt-2 text-xs text-error text-center">{error}</p>
          )}

          {/* Quick value buttons */}
          <div className="flex justify-center gap-2 mt-3">
            {[1, 5, 10, 20].map((quick) => (
              <button
                key={quick}
                onClick={() => setValue(String(quick))}
                className="px-3 py-1 text-sm font-mono bg-background-elevated hover:bg-background-primary
                  text-text-secondary hover:text-text-primary rounded transition-colors"
              >
                {quick}
              </button>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 p-4 pt-0">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 font-display text-sm uppercase tracking-wide
              bg-background-elevated hover:bg-background-primary text-text-secondary
              hover:text-text-primary rounded transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className={`flex-1 px-4 py-2 font-display text-sm uppercase tracking-wide
              ${styles.button} rounded transition-colors shadow-lg`}
          >
            {submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
